import * as amplitudeSdk from "@amplitude/analytics-browser";
import type { AxiosInstance } from "axios";
import axios from "axios";

// Re-exported as a value: Babel's TypeScript transform drops
// `export { namespaceImport }`, which breaks the webpack build.
export const amplitude = amplitudeSdk;

let initialized = false;

export type ExceptionContext = Record<string, unknown>;

const normalizeError = (
  error: unknown,
): { message: string; stack?: string; name?: string } => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message?: unknown }).message ?? "Unknown error");
    const stack =
      "stack" in error ? String((error as { stack?: unknown }).stack ?? "") : undefined;
    const name =
      "name" in error ? String((error as { name?: unknown }).name ?? "") : undefined;

    return {
      message,
      stack: stack || undefined,
      name: name || undefined,
    };
  }

  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: "Unknown error" };
  }
};

export const trackException = (
  error: unknown,
  context: ExceptionContext = {},
): void => {
  if (!initialized || typeof window === "undefined") {
    return;
  }

  const normalized = normalizeError(error);

  amplitude.track("Exception", {
    message: normalized.message,
    stack: normalized.stack?.slice(0, 2000),
    name: normalized.name,
    url: window.location.href,
    path: window.location.pathname,
    ...context,
  });
};

const attachAxiosExceptionTracking = (
  instance: AxiosInstance,
  source: string,
): void => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      trackException(error, {
        source,
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
      });
      return Promise.reject(error);
    },
  );
};

export const initAmplitudeAnalytics = (): void => {
  if (initialized || typeof window === "undefined") {
    return;
  }

  const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
  if (!apiKey) {
    return;
  }

  amplitude.init(apiKey, {
    autocapture: {
      attribution: true,
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
      elementInteractions: true,
      networkTracking: true,
      webVitals: true,
      frustrationInteractions: true,
    },
  });

  attachAxiosExceptionTracking(axios, "axios");
  initialized = true;
};

export const initGlobalExceptionTracking = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.addEventListener("error", (event) => {
    trackException(event.error ?? event.message, {
      source: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    trackException(event.reason, {
      source: "unhandledrejection",
    });
  });
};

export const attachApiExceptionTracking = (api: AxiosInstance): void => {
  attachAxiosExceptionTracking(api, "api");
};
